import { z } from "zod";
import { StopType, DeliveryStatus, WaypointAction } from "@carry/shared";
import { PublicIdSchema } from "./common";
import { OrderItem } from "./order";
import { UnavailableTimeWindow } from "./pickup-spot";

export const RouteStatus = z.enum(["draft", "assigned", "confirmed", "started", "completed"]);

export const UnresolvedDelaySummary = z.object({
  id: PublicIdSchema,
  stop_id: PublicIdSchema,
  delay_reason: z.enum(["traffic", "production_delay", "previous_case_delay", "other"]),
  eta_type: z.enum(["unknown", "specified"]),
  eta_at: z.iso.datetime({ offset: true }).nullable(),
  impact_note: z.string().nullable(),
  reported_by: z.object({ id: PublicIdSchema, name: z.string() }),
  reported_at: z.iso.datetime({ offset: true }),
});

export const RouteStop = z.object({
  id: PublicIdSchema,
  stop_type: StopType,
  sequence: z.number().int(),
  status: DeliveryStatus,
  location_latitude: z.string().nullable(),
  location_longitude: z.string().nullable(),
  estimated_arrival: z.iso.datetime({ offset: true }).nullable(),
  estimated_departure: z.iso.datetime({ offset: true }).nullable(),
  actual_arrival: z.iso.datetime({ offset: true }).nullable(),
  actual_departure: z.iso.datetime({ offset: true }).nullable(),
  /** F29 委託先支払距離: 到着時にドライバーが入力した ODO (km, 整数). 未入力は null */
  arrival_odometer: z.number().int().nullable().optional(),
  /**
   * #769: 過去日の実績を後追い登録 (承認) した時刻。当日打刻の stop は null。
   * 画面の「後追い」バッジはこの値だけを見る (当日打刻と見え方を変えるため)。
   */
  backfilled_at: z.iso.datetime({ offset: true }).nullable().optional(),
  /**
   * 経由地 (`stop_type='waypoint'`) での作業種別 (#724)。経由地以外は null。
   * `load` の経由地は 3 時間ルールの起点になるため、画面・保存ゲートの判定に必要。
   */
  waypoint_action: WaypointAction.nullable().optional(),
  /**
   * 経由地 stop の元になった `order_locations` 行の訪問順 (#755)。
   * レーン単体最適化で「どの経由地か」を特定し、その立寄り可能時間・作業時間を送るのに使う。
   */
  order_location_sequence: z.number().int().nullable().optional(),
  /** 表示用: 配送先/集荷先/ハブの名称 */
  name: z.string().nullable().optional(),
  /** 表示用: 住所 */
  address: z.string().nullable().optional(),
  /** 表示用: 取引先 (clients) 名 */
  client_name: z.string().nullable().optional(),
  /** 表示用: 顧客 (deliverySpots) 名. 個人配送に使用 */
  delivery_name: z.string().nullable().optional(),
  /** 表示用: 連絡先電話番号 */
  contact_phone: z.string().nullable().optional(),
  /** 表示用: 特記事項 (orders.remarks) */
  note: z.string().nullable().optional(),
  /** 紐付く注文の public_id (ペア削除に使用) */
  order_id: PublicIdSchema.nullable().optional(),
  /** 表示用: 紐付く注文の注文番号 (orders.order_number) */
  order_number: z.string().nullable().optional(),
  /** 表示用: 紐付く注文の取引先会社名 (orders.delivery_company) */
  delivery_company: z.string().nullable().optional(),
  /** 表示用: 紐付く注文の取引先支店 (orders.delivery_branch) */
  delivery_branch: z.string().nullable().optional(),
  /** 表示用: 紐付く注文のお客様名 (orders.delivery_person_name) */
  delivery_person_name: z.string().nullable().optional(),
  /** 表示用: 紐付く注文の店舗名 (orders.store_name) */
  store_name: z.string().nullable().optional(),
  /** 表示用: 注文の集荷時間指定 (HH:MM 開始) */
  pickup_time_start: z.string().nullable().optional(),
  /** 表示用: 注文の集荷時間指定 (HH:MM 終了) */
  pickup_time_end: z.string().nullable().optional(),
  /** 表示用: 紐付く注文の集荷不可時間 */
  pickup_unavailable_times: z.array(UnavailableTimeWindow).optional(),
  /** 表示用: 注文のお届け時間指定 (HH:MM 開始) */
  delivery_time_start: z.string().nullable().optional(),
  /** 表示用: 注文のお届け時間指定 (HH:MM 終了) */
  delivery_time_end: z.string().nullable().optional(),
  /**
   * 紐付く注文の積載時間の上限 (分)。null / 未指定はシステム設定の既定 (3 時間, #767)。
   * 3 時間ルールの判定・バナー文言が**注文ごとの上限**を使うのに要る (表示専用ではない)。
   */
  max_ride_minutes: z.number().int().nullable().optional(),
  /** 表示用: 紐付く注文の商品明細 (order_items). 商品タブで使用 */
  items: z.array(OrderItem).optional(),
  /** 表示用: お届け時対応 (orders.handover_location) */
  handover_location: z.string().nullable().optional(),
  /** 表示用: 支払方法 (orders.payment_type) */
  payment_type: z.string().nullable().optional(),
  /** 表示用: 支払番号 (orders.payment_number) */
  payment_number: z.string().nullable().optional(),
  /** 表示用: 到着時の注意事項 (orders.arrival_instructions) */
  arrival_instructions: z.string().nullable().optional(),
  /** 表示用: 取引先電話 (clients.phone). ドライバーアプリから遅延時などに発信する */
  client_phone: z.string().nullable().optional(),
  /** 表示用: 当日担当者 (order_delivery_info.day_of_contact_person) */
  day_of_contact_person: z.string().nullable().optional(),
  /** 表示用: 当日連絡先 (order_delivery_info.day_of_contact_phone) */
  day_of_contact_phone: z.string().nullable().optional(),
  /** 表示用: 梱包・包装方法 (orders.delivery_packaging) */
  delivery_packaging: z.string().nullable().optional(),
  /** 表示用: 顧客への連絡事項 (orders.delivery_notes) */
  delivery_notes: z.string().nullable().optional(),
  /** 表示用: 店舗へのメッセージ (orders.store_message) */
  store_message: z.string().nullable().optional(),
  /** 表示用: 建物名・部屋番号 (orders.delivery_building) */
  delivery_building: z.string().nullable().optional(),
  /** 表示用: 店舗電話 (orders.store_phone) */
  store_phone: z.string().nullable().optional(),
  /** 表示用: 宛名フリガナ (orders.delivery_person_name_kana) */
  delivery_person_name_kana: z.string().nullable().optional(),
  /** 表示用: 顧客メール (orders.delivery_email) */
  delivery_email: z.string().nullable().optional(),
  /** 表示用: 搬入口/駐車場情報 (pickup_spots.remarks) */
  spot_remarks: z.string().nullable().optional(),
  /** 表示用: MR/特記事項 (orders.remarks) */
  delivery_remarks: z.string().nullable().optional(),
  /** 同一集荷先のグルーピングキー (集荷住所を正規化). バッチ対象外は null. 詳細: services/pickup-batch.ts */
  pickup_batch_key: z.string().nullable().optional(),
  /** 表示用: 納品先マスタの注意事項 (delivery_spots.caution_notes). null の場合は非表示 */
  caution_notes: z.string().nullable().optional(),
  /** 表示用: stop 上での作業時間 (秒). 注文個別や拠点既定が無い場合は null */
  service_time_seconds: z.number().int().nullable().optional(),
  /**
   * 直前 stop (先頭 stop はデポ=車両保管場所/営業所) からの補正後移動時間 (秒).
   * recalc がサーバ側で確定し保存する正の値. 計算不可 (座標欠落/OSRM 失敗) 時は null.
   */
  travel_seconds_from_prev: z.number().int().nullable().optional(),
  /** 直前 stop からの OSRM 生移動時間 (秒). multiplier / buffer 適用前。計算不可時は null. */
  raw_travel_seconds_from_prev: z.number().int().nullable().optional(),
  /** 直前 stop からの純走行時間 (秒). OSRM 生値へ学習係数だけを1回適用。計算不可時は null. */
  pure_travel_seconds_from_prev: z.number().int().nullable().optional(),
  /** 直前 stop からの OSRM 走行距離 (m). 計算不可時は null. */
  travel_distance_m_from_prev: z.number().int().nullable().optional(),
  /**
   * #729: 直前 stop からこの stop へ向かう区間で高速道路の利用を許可するか。
   * true の区間だけ OSRM を高速ありで引き直す。既定 false は一般道限定 (Issue #729 以前と同じ)。
   * 高速代は経費 (expense_type='highway') として別途登録・請求する。
   */
  allow_motorway_from_prev: z.boolean().optional(),
  /** 管理画面向け: 同一 stop の最新未解決遅延報告。 */
  unresolved_delay: UnresolvedDelaySummary.nullable().optional(),
});

/**
 * ルートの経路計算状態 (DTO 導出値, DB には保存しない).
 * - ok: 全 stop に座標があり OSRM 再計算も成立 (per-stop の travel が確定).
 * - missing_coordinates: 座標欠落 stop がある (計算不可区間あり).
 * - osrm_unavailable: 座標は揃っているが OSRM 失敗/未設定で travel が確定できない.
 */
export const RouteCalcStatus = z.enum(["ok", "osrm_unavailable", "missing_coordinates"]);

/**
 * 到着予定時刻の算出に使った出発基準の由来 (DTO 導出値, DB には保存しない).
 * - planned: planned_departure_at (配車担当の手入力). 最優先で使う.
 * - derived: サーバが推定した基準 (最適化が書いた先頭 stop の到着時刻、または便区分の開始時刻).
 * - none: 基準を解決できず到着予定時刻を算出していない.
 */
export const DepartureBasis = z.enum(["planned", "derived", "none"]);

/**
 * planned_departure_at の由来 (#614)。
 * - manual:    配車担当が画面で入力した。再最適化・再計算でも上書きしない。
 * - optimized: 最適化 (route.optimize / optimization.confirm) が書いた。再最適化で更新する。
 *
 * 由来を記録する前に保存された行は null。手入力側 (上書きしない) として扱う。
 */
export const PlannedDepartureSource = z.enum(["manual", "optimized"]);

/**
 * 経路再計算後の警告 (操作自体は reject しない). 現状は指定時間窓違反のみ.
 */
export const RouteWarning = z.object({
  /** 警告対象 stop の public_id */
  stop_public_id: PublicIdSchema,
  type: z.literal("time_window_violation"),
  /** 指定時間窓の表示文字列 (例 "12:00-12:30", JST) */
  expected_window: z.string(),
  /** 推定到着時刻 (ISO8601, offset 付き) */
  estimated_arrival: z.iso.datetime({ offset: true }),
});

/**
 * 自動最適化 (addOrder/removeOrder/moveOrder) の結果ステータス (#526)。
 * mutation 自体は常に成功し、UI はこの値で警告表示を切り替える。
 * サーバは最適化を応答内で待ち切るため、未確定を表す値は持たない。
 * - ok: 制約 (3 時間・遅着) を満たす順序を適用した。
 * - skipped: 最適化の余地が無く (stop 2 件以下) 再計算のみ実施した。
 * - infeasible: 制約を満たす順序が見つからず、順序は元のまま。
 * - unavailable: optimizer 不達・座標欠落・再計算失敗で最適化できなかった。
 * - unsupported: クロスドック等で未対応。
 * - locked: 確定済みレーンのため変更しなかった。
 */
export const LaneOptimizeStatus = z.enum([
  "ok",
  "skipped",
  "infeasible",
  "unavailable",
  "unsupported",
  "locked",
]);

/** ルートの出発・帰着地点。車両保管場所を優先し、未設定時は営業所を使う。 */
export const RouteDepot = z.object({
  name: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  latitude: z.number(),
  longitude: z.number(),
});

// レーン (route) は日単位 (日 × ドライバー × 車両) の実体 (#425)。便の所属は持たず、
// 表示・帳票用の便は stop の時刻から導出する。
export const Route = z.object({
  id: PublicIdSchema,
  delivery_plan_id: PublicIdSchema,
  driver_id: PublicIdSchema.nullable(),
  vehicle_id: PublicIdSchema.nullable(),
  delivery_date: z.iso.date(),
  total_distance_m: z.number().int(),
  estimated_duration_min: z.number().int(),
  status: RouteStatus,
  stops: z.array(RouteStop).default([]),
  /** 帰着予定時刻 (最終 stop 出発 + 帰着 leg, ISO8601). 計算不可時は null. */
  estimated_return_at: z.iso.datetime({ offset: true }).nullable().optional(),
  /** 帰着 leg の OSRM 生移動時間 (秒). */
  raw_return_travel_seconds: z.number().int().nullable().optional(),
  /** 帰着 leg の学習補正済み純走行時間 (秒). */
  pure_return_travel_seconds: z.number().int().nullable().optional(),
  /** 帰着 leg の到着予測用移動時間 (秒). 固定倍率・余裕を含む. */
  return_travel_seconds: z.number().int().nullable().optional(),
  /** 帰着 leg の OSRM 走行距離 (m). */
  return_travel_distance_m: z.number().int().nullable().optional(),
  /**
   * 出発予定時刻 (デポ発, ISO8601). null = 未設定 (サーバが基準時刻を推定する).
   * 手入力 (planned_departure_source='manual') の場合だけ再計算の出発基準として最優先され、
   * 最適化を実行しても上書きされない. 最適化が書いた値は再最適化で更新される (#614).
   */
  planned_departure_at: z.iso.datetime({ offset: true }).nullable().optional(),
  /** planned_departure_at の由来 (#614). 詳細は PlannedDepartureSource 参照. */
  planned_departure_source: PlannedDepartureSource.nullable().optional(),
  /**
   * 到着予定時刻の算出に使った出発基準の由来 (導出値). 詳細は DepartureBasis 参照.
   * サーバは常に値を返すが、既存の RouteDto リテラル (テストフィクスチャ等) を壊さないよう
   * 任意フィールドとして定義する. 読み手は未指定を "none" として扱う.
   */
  departure_basis: DepartureBasis.optional(),
  /** 経路計算状態 (導出値). 既定は ok. */
  calc_status: RouteCalcStatus.default("ok"),
  /** 再計算後の警告 (指定時間窓違反等). 無ければ空配列. */
  warnings: z.array(RouteWarning).default([]),
  /** 表示・経路描画用の出発地点。解決できない場合は null。 */
  depot: RouteDepot.nullable().optional(),
  /**
   * 確定解除 (confirmed → assigned) された時刻 (#523). null = 一度も公開していない.
   * status='assigned' かつ非 null は「ドライバーへ公開済みだが編集中に戻ったレーン」を表し、
   * ドライバーアプリは編集中バナーを出して打刻を止める. 確定すると null に戻る.
   */
  unconfirmed_at: z.iso.datetime({ offset: true }).nullable().optional(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
});

export const CreateRouteInput = z.object({
  plan_id: PublicIdSchema,
});

/**
 * ドライバー/車両割当の入力.
 * - driver_id が同一 plan 内の他ルートに割当済みの場合は 409 CONFLICT
 *   (code: DRIVER_ALREADY_ASSIGNED, existing_route_id) で拒否する (Issue #400)。
 *   付け替えは route.unassignDriver で旧ルートの割当を外してから行う。
 * - vehicle_id 省略時はサーバが自動割当する (①ドライバーの所有車 → ②未使用リース車 → ③未割当).
 * - vehicle_id 指定時はその車両を割当てる. 同一 plan 内の他ルートが使用中なら奪い、
 *   奪われた側へ代替車両を自動割当する (詳細: AssignNotice).
 */
export const AssignDriverInput = z.object({
  driver_id: PublicIdSchema,
  vehicle_id: PublicIdSchema.optional(),
});

/**
 * assign の副作用通知. 同一 plan 内の他ルートに対して行った自動変更を表す.
 * - vehicle_reassigned: 指定車両を使用していた旧ルートへ代替車両を自動割当した.
 * - vehicle_unassigned: 代替車両が無く旧ルートの車両が未割当になった.
 *
 * driver_released は廃止 (Issue #400): 同一 plan 内の重複割当は自動解除ではなく
 * DRIVER_ALREADY_ASSIGNED で拒否する。
 */
export const AssignNotice = z.object({
  type: z.enum(["vehicle_reassigned", "vehicle_unassigned"]),
  /** 影響を受けたルート (旧レーン) の public_id */
  route_id: PublicIdSchema,
  /** vehicle_reassigned のとき: 新しく割当てた車両の public_id */
  vehicle_id: PublicIdSchema.nullable().optional(),
  /** vehicle_reassigned のとき: 表示用の車両名 (vehicle_name または vehicle_number) */
  vehicle_label: z.string().nullable().optional(),
});

export const ReorderStopsInput = z.object({
  stop_ids: z.array(PublicIdSchema).min(1),
});

/**
 * #729: 区間 (直前 stop → 指定 stop) の高速道路利用可否を切り替える入力。
 * 切り替えの粒度が区間なのは、OSRM の exclude がリクエスト単位でしか効かないため。
 */
export const SetStopAllowMotorwayInput = z.object({
  stop_id: PublicIdSchema,
  allow_motorway: z.boolean(),
});

export const AddOrderToRouteInput = z.object({
  order_id: PublicIdSchema,
  /** 挿入位置 (0 起点)。省略時は末尾に追加 (現行互換)。pickup+delivery がこの位置から連続で入る。 */
  target_sequence: z.number().int().min(0).optional(),
});

/**
 * 注文 (order) 単位で別レーン (ルート) へ移動するときの入力.
 * 対象注文に紐づく全 stop (pickup + delivery) をまとめて移動先ルート末尾へ移す.
 * 移動元・移動先は同一配送計画 (delivery_plan) 内に限り、確定前 (draft/assigned) のみ可.
 * 同一計画内で 1 注文 1 レーンを保つため、移動先に同じ注文が既にある場合は拒否する.
 */
export const MoveOrderInput = z.object({
  /** 移動先ルートの public_id */
  to_route_id: PublicIdSchema,
  /** 移動先ルートでの挿入位置 (0 起点). 省略時は末尾に追加. pickup+delivery がこの位置から連続で入る. */
  target_sequence: z.number().int().min(0).optional(),
  /** 学習ログ (route_adjustments) に残す補足理由 (任意). */
  reason_note: z.string().max(500).optional(),
});

/** 移動後の情報提供 warning (移動自体は reject しない). */
export const MoveStopWarning = z.object({
  type: z.literal("over_capacity"),
  /** 警告対象ルートの public_id */
  route_id: PublicIdSchema,
  /**
   * ルートの最大同時積載 (kg)。#428: 全日レーンでは配達で荷が減るため、
   * 立寄り順に pickup で加算・delivery で減算した系列の最大値
   * (delivery-only 注文は出発時積載)。フィールド名は互換のため据え置き。
   */
  total_weight_kg: z.number(),
  /** 車両の積載上限 (kg) */
  max_load_kg: z.number(),
});

/**
 * driver が自分の stop の ステータスを更新するときの入力.
 * actual_arrival / actual_departure はいずれかを更新.
 */
export const UpdateStopStatusInput = z.object({
  status: DeliveryStatus.optional(),
  actual_arrival: z.iso.datetime({ offset: true }).nullable().optional(),
  actual_departure: z.iso.datetime({ offset: true }).nullable().optional(),
  /**
   * F29 委託先支払距離: 到着打刻時にドライバーが入力する ODO (km, 整数). 配達先ごとの実走区間を
   * 直前ストップとの差分で求め, 支払距離の按分に用いる. 未入力可 (null/未指定)。
   */
  arrival_odometer: z.number().int().nonnegative().nullable().optional(),
  /** 遅延報告: 予定到着時刻の上書き (ドライバー側から) */
  estimated_arrival: z.iso.datetime({ offset: true }).nullable().optional(),
  estimated_departure: z.iso.datetime({ offset: true }).nullable().optional(),
  /** 取消などステータス更新理由。delivery_status_histories.comment に保存する。 */
  comment: z.string().trim().min(1).max(500).optional(),
  /**
   * F10 連携: ステータス変更時のドライバー GPS 現在地 (監査用)。driver アプリが best-effort で
   * 送る。delivery_status_histories に保存される。取得失敗/未許可時は未指定。
   */
  latitude: z.string().optional(),
  longitude: z.string().optional(),
});

/**
 * レーン (ルート) の出発予定時刻を手入力する入力.
 * - departure_at 指定時: デポ出発時刻として保存し、再計算の出発基準に使う.
 * - departure_at = null: 手入力を解除し、先頭 stop の estimated_arrival → 便区分開始時刻の
 *   フォールバックに戻す.
 */
export const SetRouteDepartureInput = z.object({
  departure_at: z.iso.datetime({ offset: true }).nullable(),
});

export const ListDriverTodayRoutesInput = z.object({
  /** YYYY-MM-DD. 未指定時は JST today */
  delivery_date: z.iso.date().optional(),
});

/** 道路に沿った経路 (OSRM /route の結果). coordinates は GeoJSON 順 ([longitude, latitude]). */
export const RoutePath = z.object({
  coordinates: z.array(z.tuple([z.number(), z.number()])),
  distance_m: z.number().int(),
  duration_s: z.number().int(),
  /** "osrm" = 道路に沿った曲線。OSRM 不達時は API エラーにする。 */
  source: z.literal("osrm"),
  /** coordinates の先頭に使った出発地点。解決できない場合は null。 */
  depot: RouteDepot.nullable().optional(),
});

export type RouteDto = z.infer<typeof Route>;
export type AssignNoticeDto = z.infer<typeof AssignNotice>;
export type RouteStopDto = z.infer<typeof RouteStop>;
export type RouteDepotDto = z.infer<typeof RouteDepot>;
export type RouteCalcStatusValue = z.infer<typeof RouteCalcStatus>;
export type DepartureBasisValue = z.infer<typeof DepartureBasis>;
export type PlannedDepartureSourceValue = z.infer<typeof PlannedDepartureSource>;
export type LaneOptimizeStatusValue = z.infer<typeof LaneOptimizeStatus>;
export type SetRouteDepartureInputDto = z.infer<typeof SetRouteDepartureInput>;
export type RouteWarningDto = z.infer<typeof RouteWarning>;
export type RoutePathDto = z.infer<typeof RoutePath>;
export type UpdateStopStatusInputDto = z.infer<typeof UpdateStopStatusInput>;
export type MoveOrderInputDto = z.infer<typeof MoveOrderInput>;
export type MoveStopWarningDto = z.infer<typeof MoveStopWarning>;
